import qs from 'qs'
import {
    SetStateAction,
    Dispatch,
    useCallback,
    useEffect,
    useState,
} from 'react'
import { DeleteOutlined } from '@ant-design/icons'
import { Popover, Tooltip } from 'antd'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'

import Link from '~/core/Link'
import { getUnsavedImDfm3dSlides } from '~/redux-modules/unsaved-im-dfm-3d-slides/selector'
import { UnsavedSlideStateType } from '~/redux-modules/unsaved-im-dfm-3d-slides/reducer'

import ConfirmDeletePopoverContent from './ConfirmDeletePopoverContent'
import styles from './styles.css'

const TEXTAREA_ELEMENT = 'textarea'
const INPUT_ELEMENT = 'input'

type DeletePopoverPropsType = {
    dfmFeedbackId?: string | null
    idx: string
    setActiveSlideIndex?: Dispatch<SetStateAction<number>>
    isUnsaved?: boolean
}
type ParamsType = {
    id: string
}

const DEFAULT_SLIDE_NUMBER = '1'
const DELETE_KEY_CODE = 46
const BACKSPACE_KEY_CODE = 8

const DeleteSlideIcon = (props: DeletePopoverPropsType) => {
    const { dfmFeedbackId, idx, setActiveSlideIndex, isUnsaved } = props

    const { id: qmrId = '' } = useParams<ParamsType>()

    const unsavedImDfm3dSlides = useSelector((state: UnsavedSlideStateType) => {
        if (state.unsavedImDfm3dSlides) {
            return getUnsavedImDfm3dSlides(state, qmrId)
        }
    })

    const haveUnsaved3dSlide = unsavedImDfm3dSlides?.length > 0

    const [isPopoverVisible, setIsPopoverVisible] = useState(false)
    const [deleteKeyClicked, setDeleteKeyClicked] = useState(false)

    const togglePopover = () => {
        if (!deleteKeyClicked) {
            setIsPopoverVisible(!isPopoverVisible)
        }
        if (deleteKeyClicked) {
            setDeleteKeyClicked(!deleteKeyClicked)
        }
    }

    const currentQueryParams = qs.parse(location.search)

    const slideNumber = currentQueryParams.slide
        ? currentQueryParams.slide
        : DEFAULT_SLIDE_NUMBER

    const handleDeleteKey = useCallback(
        (evt: { keyCode: number }) => {
            setIsPopoverVisible(false)

            if (slideNumber === idx) {
                const activeElement = document.activeElement

                const isTextareaFocused =
                    activeElement?.tagName.toLowerCase() === TEXTAREA_ELEMENT
                const isInputFocused =
                    activeElement?.tagName.toLowerCase() === INPUT_ELEMENT

                if (
                    evt.keyCode === DELETE_KEY_CODE ||
                    (evt.keyCode === BACKSPACE_KEY_CODE &&
                        !isTextareaFocused &&
                        !isInputFocused)
                ) {
                    setDeleteKeyClicked(true)
                }
            }
        },
        [idx, slideNumber],
    )

    const popoverVisible =
        (deleteKeyClicked && slideNumber === idx) || isPopoverVisible

    useEffect(() => {
        document.addEventListener('keydown', handleDeleteKey, false)
        return () => document.removeEventListener('keydown', handleDeleteKey)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <Popover
            content={
                <ConfirmDeletePopoverContent
                    isUnsaved={isUnsaved}
                    dfmFeedbackId={dfmFeedbackId}
                    idx={idx}
                    setActiveSlideIndex={setActiveSlideIndex}
                    togglePopover={togglePopover}
                    setDeleteKeyClicked={setDeleteKeyClicked}
                />
            }
            onVisibleChange={togglePopover}
            overlayStyle={{ maxWidth: '322px' }}
            placement="left"
            trigger="click"
            visible={popoverVisible}
        >
            <Tooltip placement="top" title={haveUnsaved3dSlide ? '' : 'Delete'}>
                <Link
                    name="delete-slide"
                    onClick={(evt: Event) => evt.stopPropagation()}
                    placement="im-dfm-slide"
                    disabled={haveUnsaved3dSlide && !isUnsaved}
                >
                    <DeleteOutlined
                        className={styles.deleteIcon}
                        data-test-target="delete-icon"
                    />
                </Link>
            </Tooltip>
        </Popover>
    )
}

export default DeleteSlideIcon
